import { Plus, X } from 'lucide-react';
import React, { ReactNode } from 'react';

export interface Tab {
  id: string;
  label: string;
  content: ReactNode;
  closable?: boolean;
}

export interface TabsProps {
  tabs: Tab[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  onTabClose?: (tabId: string) => void;
  onAddTab?: () => void;
  headerRight?: ReactNode;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  activeTabId,
  onTabChange,
  onTabClose,
  onAddTab,
  headerRight,
  className = '',
}) => {
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  return (
    <div
      className={`flex flex-col h-full overflow-hidden max-h-full ${className}`}
    >
      {/* Tab Headers */}
      <div className="flex items-center border-b border-light-200 dark:border-graphite-700 bg-white dark:bg-graphite-800 shadow-sm flex-shrink-0">
        {tabs.map(tab => (
          <div
            key={tab.id}
            className={`group flex items-center px-4 py-1.5 border-b-2 transition-all duration-200 cursor-pointer relative ${
              activeTabId === tab.id
                ? 'border-primary-600 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                : 'border-transparent hover:bg-light-50 dark:hover:bg-graphite-700 text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-500 dark:hover:text-light-200'
            }`}
            onClick={() => onTabChange(tab.id)}
          >
            <span className="text-sm font-medium truncate max-w-40 pr-2">
              {tab.label}
            </span>
            {tab.closable && onTabClose && (
              <button
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2 p-1 rounded-full hover:bg-light-200 dark:hover:bg-graphite-600 hover:text-deepTechBlue-500 dark:hover:text-light-300"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onTabClose(tab.id);
                }}
                title="Fechar conversa"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {onAddTab && (
          <button
            onClick={onAddTab}
            className="flex items-center px-3 py-1.5 border-b-2 border-transparent hover:bg-light-50 dark:hover:bg-graphite-700 text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-500 dark:hover:text-light-300 transition-all duration-200"
            title="Nova conversa"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}

        {headerRight && (
          <div className="ml-auto pr-2 h-full flex items-center">{headerRight}</div>
        )}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden min-h-0">
        {activeTab && (
          <div className="h-full overflow-hidden">{activeTab.content}</div>
        )}
      </div>
    </div>
  );
};
